import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Button, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch, type Control } from 'react-hook-form';
import type {
  Certificaattype,
  EisInvoer,
  Eissoort,
  Functietitel,
  Opleidingsniveau,
  Rijbewijs,
  Taalniveau,
  VacatureDetails,
  Vaardigheid,
  Vaardigheidsniveau,
} from '../../api/typen.ts';
import { useVacatureMutaties } from '../../api/vacatures.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { CertificaattypeVeld, FunctietitelVeld, VaardigheidVeld } from '../../componenten/keuzevelden.tsx';
import { Keuzeveld, Tekstveld, Vinkveld } from '../../componenten/velden.tsx';
import { naarGetal, naarTekst } from '../../helpers/formulier.ts';
import {
  eissoortLabels,
  opleidingsniveauLabels,
  opties,
  rijbewijsLabels,
  taalLabels,
  taalniveauLabels,
  vaardigheidsniveauLabels,
} from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface EisRij {
  soort: Eissoort;
  functietitel: Functietitel | null;
  vaardigheid: Vaardigheid | null;
  certificaattype: Certificaattype | null;
  opleidingsniveau: Opleidingsniveau | '';
  taalcode: string;
  rijbewijs: Rijbewijs | '';
  minimaalJaren: string;
  minimaalVaardigheidsniveau: Vaardigheidsniveau | '';
  minimaalTaalniveau: Taalniveau | '';
  verplicht: boolean;
}

interface Waarden {
  rijen: EisRij[];
}

const soortOpties = opties(eissoortLabels);
const opleidingsOpties = opties(opleidingsniveauLabels);
const vaardigheidsniveauOpties = opties(vaardigheidsniveauLabels);
const taalniveauOpties = opties(taalniveauLabels);
const taalOpties = opties(taalLabels);
const rijbewijsOpties = opties(rijbewijsLabels);

function legeRij(soort: Eissoort = 'ervaring'): EisRij {
  return {
    soort,
    functietitel: null,
    vaardigheid: null,
    certificaattype: null,
    opleidingsniveau: '',
    taalcode: '',
    rijbewijs: '',
    minimaalJaren: '',
    minimaalVaardigheidsniveau: '',
    minimaalTaalniveau: '',
    verplicht: true,
  };
}

function beginwaarden(eisen: VacatureDetails['eisen']): Waarden {
  return {
    rijen: eisen.map((eis) => ({
      soort: eis.soort,
      functietitel: eis.functietitel,
      vaardigheid: eis.vaardigheid,
      certificaattype: eis.certificaattype,
      opleidingsniveau: eis.opleidingsniveau ?? '',
      taalcode: eis.taalcode ?? '',
      rijbewijs: eis.rijbewijs ?? '',
      minimaalJaren: naarTekst(eis.minimaalJaren),
      minimaalVaardigheidsniveau: eis.minimaalVaardigheidsniveau ?? '',
      minimaalTaalniveau: eis.minimaalTaalniveau ?? '',
      verplicht: eis.verplicht,
    })),
  };
}

function naarInvoer(rij: EisRij, index: number): EisInvoer {
  const nummer = index + 1;
  switch (rij.soort) {
    case 'ervaring':
      if (!rij.functietitel) {
        throw new Error(`Kies een functietitel bij eis ${nummer}.`);
      }
      return {
        soort: 'ervaring',
        functietitelId: rij.functietitel.id,
        minimaalJaren: naarGetal(rij.minimaalJaren),
        verplicht: rij.verplicht,
      };
    case 'vaardigheid':
      if (!rij.vaardigheid) {
        throw new Error(`Kies een vaardigheid bij eis ${nummer}.`);
      }
      return {
        soort: 'vaardigheid',
        vaardigheidId: rij.vaardigheid.id,
        minimaalVaardigheidsniveau: rij.minimaalVaardigheidsniveau || null,
        verplicht: rij.verplicht,
      };
    case 'certificaat':
      if (!rij.certificaattype) {
        throw new Error(`Kies een certificaattype bij eis ${nummer}.`);
      }
      return { soort: 'certificaat', certificaattypeId: rij.certificaattype.id, verplicht: rij.verplicht };
    case 'opleiding':
      if (!rij.opleidingsniveau) {
        throw new Error(`Kies een opleidingsniveau bij eis ${nummer}.`);
      }
      return { soort: 'opleiding', opleidingsniveau: rij.opleidingsniveau, verplicht: rij.verplicht };
    case 'taal':
      if (!rij.taalcode) {
        throw new Error(`Kies een taal bij eis ${nummer}.`);
      }
      return {
        soort: 'taal',
        taalcode: rij.taalcode,
        minimaalTaalniveau: rij.minimaalTaalniveau || null,
        verplicht: rij.verplicht,
      };
    case 'rijbewijs':
      if (!rij.rijbewijs) {
        throw new Error(`Kies een rijbewijs bij eis ${nummer}.`);
      }
      return { soort: 'rijbewijs', rijbewijs: rij.rijbewijs, verplicht: rij.verplicht };
  }
}

function EisVelden({ index, control }: { index: number; control: Control<Waarden> }) {
  const soort = useWatch({ control, name: `rijen.${index}.soort` });
  const naam = (veld: keyof EisRij) => `rijen.${index}.${veld}`;
  return (
    <>
      {soort === 'ervaring' && (
        <>
          <Grid size={{ xs: 12, sm: 5 }}>
            <FunctietitelVeld naam={naam('functietitel')} label="Ervaring als" verplicht />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Tekstveld naam={naam('minimaalJaren')} label="Minimaal jaren" type="number" min={0} max={50} />
          </Grid>
        </>
      )}
      {soort === 'vaardigheid' && (
        <>
          <Grid size={{ xs: 12, sm: 5 }}>
            <VaardigheidVeld naam={naam('vaardigheid')} verplicht />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Keuzeveld
              naam={naam('minimaalVaardigheidsniveau')}
              label="Minimaal niveau"
              opties={vaardigheidsniveauOpties}
              leegLabel="Geen"
            />
          </Grid>
        </>
      )}
      {soort === 'certificaat' && (
        <Grid size={{ xs: 12, sm: 8 }}>
          <CertificaattypeVeld naam={naam('certificaattype')} verplicht />
        </Grid>
      )}
      {soort === 'opleiding' && (
        <Grid size={{ xs: 12, sm: 8 }}>
          <Keuzeveld
            naam={naam('opleidingsniveau')}
            label="Minimaal opleidingsniveau"
            opties={opleidingsOpties}
            verplicht
          />
        </Grid>
      )}
      {soort === 'taal' && (
        <>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Keuzeveld naam={naam('taalcode')} label="Taal" opties={taalOpties} verplicht />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Keuzeveld
              naam={naam('minimaalTaalniveau')}
              label="Minimaal niveau"
              opties={taalniveauOpties}
              leegLabel="Geen"
            />
          </Grid>
        </>
      )}
      {soort === 'rijbewijs' && (
        <Grid size={{ xs: 12, sm: 8 }}>
          <Keuzeveld naam={naam('rijbewijs')} label="Rijbewijs" opties={rijbewijsOpties} verplicht />
        </Grid>
      )}
    </>
  );
}

/**
 * Vervangt alle eisen van een vacature: ervaring, vaardigheden, certificaten, opleiding, talen en rijbewijzen,
 * elk als harde eis of als wens.
 * @param props De vacature met haar huidige eisen.
 * @returns De dialoog.
 */
export function EisenFormulier({
  open,
  onSluit,
  vacature,
}: {
  open: boolean;
  onSluit: () => void;
  vacature: VacatureDetails;
}) {
  const mutaties = useVacatureMutaties();
  const formulier = useForm<Waarden>({ defaultValues: beginwaarden(vacature.eisen) });
  const rijen = useFieldArray({ control: formulier.control, name: 'rijen' });
  useEffect(() => {
    if (open) {
      formulier.reset(beginwaarden(vacature.eisen));
    }
  }, [open, vacature.eisen, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => mutaties.zetEisen.mutateAsync({ id: vacature.id, invoer: waarden.rijen.map(naarInvoer) }),
    { melding: 'Eisen opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Eisen en wensen"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="md"
      >
        <Stack spacing={2}>
          {rijen.fields.length === 0 && (
            <Typography color="text.secondary">Nog geen eisen. Voeg er een toe.</Typography>
          )}
          {rijen.fields.map((rij, index) => (
            <Paper key={rij.id} variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Keuzeveld naam={`rijen.${index}.soort`} label="Soort" opties={soortOpties} verplicht />
                </Grid>
                <EisVelden index={index} control={formulier.control} />
                <Grid size={{ xs: 10, sm: 3 }}>
                  <Vinkveld naam={`rijen.${index}.verplicht`} label="Harde eis" hulptekst="Uit: een wens" />
                </Grid>
                <Grid size={{ xs: 2, sm: 1 }} sx={{ textAlign: 'right' }}>
                  <IconButton aria-label="Eis verwijderen" onClick={() => rijen.remove(index)}>
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button startIcon={<AddIcon />} onClick={() => rijen.append(legeRij())} sx={{ alignSelf: 'flex-start' }}>
            Eis toevoegen
          </Button>
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
